<script lang="ts">
  import type { Snippet } from 'svelte'

  import { isPanelOpen, setPanelOpen } from '../state/prefs.svelte'
  import Icon from './Icon.svelte'

  interface Props {
    /** Persistence key. Matches the Phase 0 `data-panel` values. */
    id: string
    title: string
    badge?: string | number | null
    /**
     * Whether the panel is open. The panel owns it, from the user's last
     * choice or else the default, and whatever a parent passes is replaced
     * at mount. `bind:open` is for a parent whose *script* has work to skip
     * while nobody can see the result, which is every panel that fetches.
     */
    open?: boolean
    children: Snippet
  }

  let { id, title, badge = null, open = $bindable(false), children }: Props = $props()

  // A panel's id is fixed for the life of the component, so reading the stored
  // state once at init is the intent, not a missed dependency.
  // svelte-ignore state_referenced_locally
  open = isPanelOpen(id)

  function onToggle(e: Event & { currentTarget: HTMLDetailsElement }) {
    // A `<details>` that mounts open fires `toggle` as well (Chromium and
    // WebKit, measured for the Phase 9 plan), and storing that event is how
    // every panel came to be stored as open. Only a change is the user's.
    if (e.currentTarget.open === open) return
    open = e.currentTarget.open
    setPanelOpen(id, open)
  }
</script>

<details class="panel" {open} ontoggle={onToggle}>
  <summary class="touch-target">
    <span class="title">{title}</span>
    {#if badge != null}<span class="badge">{badge}</span>{/if}
    <span class="mark"><Icon name={open ? 'minus' : 'plus'} size={14} strokeWidth={2.5} /></span>
  </summary>
  <!-- Not rendered while closed: a collapsed chart is still a chart to lay
       out, and the parents gate their requests on the same flag. -->
  {#if open}
    <div class="panel-body">
      {@render children()}
    </div>
  {/if}
</details>

<style>
  /* 1B's lower-panel row: title case at 13/800, a plus that becomes a minus,
     2 px rules, and the sidebar's 24 px gutter (plan §4.6). What goes inside
     each panel is round 2's (§8). */
  .panel {
    border-top: 2px solid var(--color-divider);
  }

  summary {
    list-style: none;
    cursor: pointer;
    user-select: none;
    min-height: var(--panel-row, 0);
    padding: 13px var(--gutter, 0px);
    font-size: var(--panel-fs, 13px);
    font-weight: 800;
    color: var(--color-text);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  summary::-webkit-details-marker,
  summary::marker {
    display: none;
    content: '';
  }

  summary:hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .title {
    flex: 1 1 auto;
    min-width: 0;
  }

  .mark {
    flex: none;
  }

  /* A square tag, as the system draws one. */
  .badge {
    flex: none;
    background: var(--color-field);
    color: var(--color-on-field);
    font-size: 11px;
    padding: 1px 6px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .panel-body {
    padding: 0 var(--gutter, 0px) 16px;
  }

  .panel-body :global(p) {
    margin: 4px 0;
    line-height: 1.45;
  }
</style>
